import { useMemo, useState } from 'react';
import { GroupView } from '@views';
import { NotificationService, CommunicationMode } from '@app/api';
import { ChatStore } from '@app/api/ChatStore';
import { useAppCookies } from '@app/providers/AppCookieProvider';
import { NoParamCallback } from 'node:fs';
import { SelectChangeEvent } from '@mui/material';

export type MessageType = 'chat' | 'program_reminder' | 'sms';

export const MESSAGE_TYPE: Record<string, MessageType> = {
  CHAT: 'chat',
  PROGRAM_REMINDER: 'program_reminder',
  SMS: 'sms',
} as const;

export const useGroupActionCell = (group: GroupView, updateSummary: NoParamCallback) => {
  const [configureSurveyDialogOpen, setConfigureSurveyDialogOpen] = useState(false);
  const [showGroupMessage, setShowGroupMessage] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const moreMenuOpen = Boolean(anchorEl);
  const [configurePledgeDialogOpen, setConfigurePledgeDialogOpen] = useState(false);
  const [groupMessage, setGroupMessage] = useState('');
  const [messageType, setMessageType] = useState<MessageType>(MESSAGE_TYPE.CHAT);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<number[]>([]);
  const cookies = useAppCookies();
  const [apSettingOpen, setAPSettingOpen] = useState(false);
  const [reportCardDialogOpen, setReportCardDialogOpen] = useState(false);
  const [engagementReportDialogOpen, setEngagementReportDialogOpen] = useState(false);

  const participants = useMemo(() => group.participants ?? [], [group.participants]);
  const allSelected = participants.length > 0 && selectedRecipientIds.length === participants.length;

  const handleToggleAll = () => {
    setSelectedRecipientIds(allSelected ? [] : participants.map((p) => p.id));
  };

  const handleToggleRecipient = (id: number) => {
    setSelectedRecipientIds((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));
  };

  const handleCloseConfigureSurveyDialogModal = () => setConfigureSurveyDialogOpen(false);

  const handleSetPledge = () => {
    setAnchorEl(null);
    setConfigurePledgeDialogOpen(true);
  };

  const handleSendMessageMenuItemClick = () => {
    setAnchorEl(null);
    setMessageType(MESSAGE_TYPE.CHAT);
    setGroupMessage('');
    setSelectedRecipientIds(participants.map((p) => p.id));
    setShowGroupMessage(true);
  };

  const handleReportCard = () => {
    setAnchorEl(null);
    setReportCardDialogOpen(true);
  };
  const handleEngagementReport = () => {
    setAnchorEl(null);
    setEngagementReportDialogOpen(true);
  };

  const isChatMessage = messageType === MESSAGE_TYPE.CHAT;

  const handleMessageTypeChange = (e: SelectChangeEvent) => {
    const newType = e.target.value as MessageType;
    setMessageType(newType);
    if (newType === MESSAGE_TYPE.CHAT) {
      setSelectedRecipientIds(participants.map((p) => p.id));
    }
  };
  const handleGroupMessageChange = (value: string) => setGroupMessage(value);
  const handleCloseGroupMessage = () => setShowGroupMessage(false);

  const handleSendMessage = async () => {
    if (selectedRecipientIds.length === 0) return;

    // Resolve cohortUser IDs to actual user IDs for the backend
    const userIds = selectedRecipientIds
      .map((cohortUserId) => participants.find((p) => p.id === cohortUserId)?.CohortUser?.userId)
      .filter((id): id is number => id != null);

    try {
      if (messageType === MESSAGE_TYPE.CHAT) {
        if (!group.channel) {
          console.error('No channel URL available for sending messages');
          return;
        }
        const chatStore = new ChatStore();
        await chatStore.PostAdminChatMessage(group.channel, groupMessage);

        await new NotificationService().sendNotification({
          groupId: Number(group.id),
          message: 'A new message was sent in your team chat! Click here to read it and respond: https://wheapp.com/',
          groupName: group.name,
          title: 'New We Hate Exercise Message',
          userId: cookies.profile.id,
          communicationMode: CommunicationMode.TEAM_CHAT,
        });
      } else if (messageType === MESSAGE_TYPE.PROGRAM_REMINDER) {
        await new NotificationService().sendNotification({
          groupId: Number(group.id),
          message: groupMessage,
          groupName: group.name,
          title: 'We Hate Exercise Reminder',
          userId: cookies.profile.id,
          communicationMode: CommunicationMode.ADMIN_PROGRAM_REMINDER,
          recipientIds: userIds,
        });
      } else if (messageType === MESSAGE_TYPE.SMS) {
        await new NotificationService().sendNotification({
          groupId: Number(group.id),
          message: groupMessage,
          groupName: group.name,
          title: 'We Hate Exercise',
          userId: cookies.profile.id,
          communicationMode: CommunicationMode.ADMIN_SMS,
          recipientIds: userIds,
        });
      }

      setShowGroupMessage(false);
      updateSummary(null);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const sendDisabled = (!isChatMessage && selectedRecipientIds.length === 0) || !groupMessage.trim();

  return {
    anchorEl,
    setAnchorEl,
    moreMenuOpen,
    configureSurveyDialogOpen,
    handleCloseConfigureSurveyDialogModal,
    configurePledgeDialogOpen,
    setConfigurePledgeDialogOpen,
    reportCardDialogOpen,
    setReportCardDialogOpen,
    engagementReportDialogOpen,
    setEngagementReportDialogOpen,
    showGroupMessage,
    groupMessage,
    messageType,
    selectedRecipientIds,
    participants,
    allSelected,
    apSettingOpen,
    setAPSettingOpen,
    sendDisabled,
    isChatMessage,
    handleToggleAll,
    handleToggleRecipient,
    handleSetPledge,
    handleSendMessageMenuItemClick,
    handleReportCard,
    handleEngagementReport,
    handleMessageTypeChange,
    handleGroupMessageChange,
    handleCloseGroupMessage,
    handleSendMessage,
  };
};
